import React, { useContext, useEffect, useState } from "react";
import MindsetResultImg from "./MindsetResultImg";
import HeaderPreview from "../../../GeneratePdf/HeaderPreview";
import FooterPreview from "../../../GeneratePdf/FooterPreview";
import SelfMindsetResultImg from '../report-pdf/SelfMindsetResultImg';
import GroupReportContext from "../../../../Context/GroupReportContext";
import { getMindsetSum } from '../../../../utils/helper';
import { useGroupReportDate } from '../../../../Context/GroupReportDate';

export default function MindsetResultSelf({ apiData, reportType, pageNumber }) {

  const [calculatedPercentage, setCalculatedPercentage] = useState(0);
  const { getPercentileFromScore } = useContext(GroupReportContext);
  const { updateMindsetscore } = useGroupReportDate();

  useEffect(() => {
    if (!apiData?.variables || !Array.isArray(apiData.variables)) {
      setCalculatedPercentage(0);
      return;
    }
    const totalScore = getMindsetSum(apiData.variables);
    setCalculatedPercentage(totalScore);
  }, [apiData]);

  const percentage = getPercentileFromScore(calculatedPercentage);
  useEffect(() => {
    updateMindsetscore(percentage);
  }, [percentage, updateMindsetscore]);

  if (!apiData) {
    return <div>Loading...</div>;
  }

  const mindsetContent = {
    growth: {
      title: 'Your Results: Adaptive & Transformative',
      // Adaptive Learner (Light Green) - 60-73%
      adaptiveBullets: [
        { label: 'Belief', text: '“My abilities can grow with effort and strategies.”' },
        { label: 'Focus', text: 'Seeks diverse challenges for development, even at the risk of failure.' },
        { label: 'Response to Feedback', text: 'Welcomes feedback as a tool for improvement.' },
        { label: 'View of Effort', text: 'Effort is necessary and rewarding for growth.' },
        { label: 'Reaction to Failure', text: 'Views failure as an opportunity to learn and adapt.' }
      ],
      // Transformative Growth (Dark Green) - 74-84%
      transformativeBullets: [
        { label: 'Belief', text: '“My potential is unlimited with persistence and learning.”' },
        { label: 'Focus', text: 'Actively pursues growth in all areas, even outside comfort zones.' },
        { label: 'Response to Feedback', text: 'Actively seeks constructive criticism to refine skills.' },
        { label: 'View of Effort', text: 'Embraces effort as the core driver of improvement.' },
        { label: 'Reaction to Failure', text: 'Thrives on failure as essential for transformation.' }
      ],
      paragraphs: [
        `Your results indicate that you embody a <b>growth mindset</b>, aligning with the Adaptive Learner and Transformative Growth levels. This mindset reflects your belief that abilities and intelligence can be developed through effort, learning, and persistence. It's a powerful perspective that turns challenges into opportunities and views setbacks as essential steps toward mastery.`,
        `With a growth mindset, you actively embrace challenges and seek out opportunities to improve, welcoming feedback as a valuable tool for growth. You recognize effort as a pathway to progress and are not discouraged by setbacks, using them as lessons to refine your approach and build resilience.`,
        `This mindset also positively impacts those around you. By valuing collaboration and viewing others' successes as sources of inspiration rather than competition, you foster supportive and innovative environments. Your ability to integrate learning and persistence contributes to stronger relationships, teamwork, and leadership.`,
        `Your commitment to continuous improvement sets the foundation for personal and professional success. By maintaining this perspective, you are well-equipped to navigate challenges, achieve meaningful growth, and inspire others to do the same. Keep leveraging this mindset to unlock even greater potential in yourself and those you influence.`
      ]
    },
    conditional: {
      title: 'Your Results: Conditional Growth',
      bullets: [
        { label: 'Belief', text: `“I can grow, but only in areas where I'm naturally talented.”` },
        { label: 'Focus', text: `Selectively takes on challenges aligned with perceived strengths.` },
        { label: 'Response to Feedback', text: `Accepts feedback when it aligns with existing beliefs.` },
        { label: 'View of Effort', text: `Effort is valuable but tied to success rather than growth.` },
        { label: 'Reaction to Failure', text: `Sees failure as situational, not personal, but avoids risk.` }
      ],
      paragraphs: [
        `Your results show that you align with a <b>mixed mindset</b>, a mindset that combines elements of both fixed and growth perspectives. This means you believe growth is possible but often within areas where you feel naturally talented or confident. You value effort, though it's often tied to achieving success rather than the process of growth itself.`,
        `At this stage, you may prefer challenges that match your strengths and feel comfortable with feedback that reinforces what you already know. While failure doesn't feel personal, you might hesitate to take risks that lead to uncertain outcomes.`,
        `To grow further, try viewing effort as a key part of learning, not just a path to success. Take small steps to embrace challenges outside your comfort zone and seek feedback that helps you stretch and improve. Your current mindset provides a strong starting point—by focusing on progress over perfection, you can build resilience and unlock greater opportunities for growth and success.`,
        `By shifting your perspective and celebrating growth as a journey, you can start to explore new possibilities beyond your natural strengths. This mindset shift will not only enhance your personal development but also positively impact your relationships, leadership, and collaboration with others. Growth isn't just about ability—it's about discovering your potential through persistence, adaptability, and a willingness to take on new challenges.`
      ]
    },
    fixed: {
      title: 'Your Results: Rigid & Cautious',
      // Rigid Self-Definition (Dark Blue) - 12-23%
      rigidBullets: [
        { label: 'Belief', text: '“My abilities and intelligence are unchangeable.”' },
        { label: 'Focus', text: 'Avoiding challenges to protect self-image.' },
        { label: 'Response to Feedback', text: 'Defensiveness or rejection of constructive criticism.' },
        { label: 'View of Effort', text: 'Sees effort as a sign of weakness.' },
        { label: 'Reaction to Failure', text: 'Internalizes failure as personal inadequacy.' }
      ],
      // Cautious Openness (Light Blue) - 24-37%
      cautiousBullets: [
        { label: 'Belief', text: '“I might improve, but only within certain limits.”' },
        { label: 'Focus', text: 'Prefers familiar challenges with predictable outcomes.' },
        { label: 'Response to Feedback', text: 'Willing to listen but may struggle to act.' },
        { label: 'View of Effort', text: 'Views effort as useful but only in specific situations.' },
        { label: 'Reaction to Failure', text: 'Avoids discussing failure but acknowledges it internally.' }
      ],
      paragraphs: [
        'Your results indicate that you may currently align with aspects of a <b>fixed mindset</b>, particularly within the Rigid Self-Definition and Cautious Openness levels. This mindset suggests a belief that abilities and intelligence are relatively static, which can sometimes limit risk-taking and openness to challenges. Recognizing this mindset is an important step toward exploring new possibilities and fostering growth.',
        'With a fixed mindset, you might prioritize avoiding challenges to maintain comfort or protect your self-image. Setbacks may feel discouraging, but they also present opportunities for reflection and growth. Shifting your perspective to see difficult tasks as chances to develop new skills can help reframe failure as part of the learning process rather than a limitation.',
        'Taking small, intentional steps can create momentum for change. Gradually embracing challenges, seeking feedback, and celebrating progress—no matter how small—can help you move beyond the limitations of a fixed mindset. These efforts encourage a more flexible and adaptive approach to personal and professional growth.',
        'This shift not only benefits your own development but also strengthens your relationships and interactions. By viewing effort as valuable and feedback as a tool for improvement, you can begin to foster resilience and confidence in both yourself and others.',
        'Remember, growth is a journey, not a destination. By making intentional changes, you can build a mindset that helps you unlock potential, strengthen connections, and achieve meaningful progress in all areas of your life.'
      ]
    }
  };

  // Get mindset category and content based on percentage
  const getMindsetData = (percentage) => {
    if (percentage >= 0 && percentage < 36) { // Fixed mindset range
      return {
        category: 'fixed',
        content: mindsetContent.fixed,
        subcategory: percentage < 16 ? 'rigid' : 'cautious' // 0-15: rigid, 16-35: cautious
      };
    } else if (percentage >= 36 && percentage < 66) { // Conditional mindset
      return {
        category: 'conditional',
        content: mindsetContent.conditional,
        subcategory: 'conditional'
      };
    } else { // Growth mindset (66-100)
      return {
        category: 'growth',
        content: mindsetContent.growth,
        subcategory: percentage >= 86 ? 'transformative' : 'adaptive' // 66-85: adaptive, 86-100: transformative
      };
    }
  };
  const getArrowPosition = (percentage) => {
    const numPercentage = Math.round(Number(percentage));

    // Match the same ranges as getMindsetData function
    if (numPercentage >= 0 && numPercentage < 16) {
      return "20%"; // Rigid Self-Definition (Dark Blue)
    } else if (numPercentage >= 16 && numPercentage < 36) {
      return "35.5%"; // Cautious Openness (Light Blue)
    } else if (numPercentage >= 36 && numPercentage < 66) {
      return "51.5%"; // Conditional Growth (Yellow)
    } else if (numPercentage >= 66 && numPercentage < 86) {
      return "66.5%"; // Adaptive Learner (Light Green)
    } else if (numPercentage >= 86 && numPercentage <= 100) {
      return "82%"; // Transformative Growth (Dark Green)
    } else if (numPercentage > 100) {
      return "82%"; // Above maximum, use Transformative
    } else if (numPercentage < 0) {
      return "19%"; // Below minimum, use Rigid
    }

    return "50%"; // Default fallback
  };

  // Render bullets based on mindset type and subcategory
  const renderMindsetBullets = (percentage) => {
    const mindsetData = getMindsetData(Math.round(percentage));
    let bulletsToRender = [];

    if (mindsetData.category === 'fixed') {
      if (mindsetData.subcategory === 'rigid') {
        bulletsToRender = mindsetData.content.rigidBullets;
      } else {
        bulletsToRender = mindsetData.content.cautiousBullets;
      }
    } else if (mindsetData.category === 'conditional') {
      bulletsToRender = mindsetData.content.bullets;
    } else { // growth
      if (mindsetData.subcategory === 'transformative') {
        bulletsToRender = mindsetData.content.transformativeBullets;
      } else {
        bulletsToRender = mindsetData.content.adaptiveBullets;
      }
    }

    return (
      <ul style={{
        listStyle: 'disc',
        paddingLeft: '9px',
        margin: 0,
        lineHeight: '1.5'
      }}>
        {bulletsToRender.map((item, idx) => (
          <li key={idx} style={{
            fontFamily: 'Helvetica, sans-serif',
            fontWeight: 400,
            fontStyle: 'normal',
            fontSize: '10px',
            lineHeight: '14px',
            letterSpacing: '0px',
            color: '#000000',
          }}>
            <span style={{ fontWeight: 700 }}>{item.label}:</span> {item.text}
          </li>
        ))}
      </ul>
    );
  };

  // Get mindset text content
  const getMindsetText = (percentage) => {
    const mindsetData = getMindsetData(Math.round(percentage));

    console.log("mindsetData.content.paragraphs", mindsetData.content.paragraphs)

    return (
      <div style={{ marginBottom: '16px' }}>
        <p style={{
          fontFamily: 'Helvetica, sans-serif',
          fontWeight: 700,
          fontStyle: 'normal',
          fontSize: '10px',
          lineHeight: '14px',
          letterSpacing: '0px',
          color: '#000000',
          margin: "0px"
        }}>
          {mindsetData.content.title}
        </p>
        {mindsetData.content.paragraphs.map((p, idx) => (
          <p
            key={idx}
            style={{
              fontFamily: 'Helvetica, sans-serif',
              fontWeight: 400,
              fontSize: '10px',
              lineHeight: '14px',
              color: '#000000',
            }}
            dangerouslySetInnerHTML={{ __html: p }}
          />
        ))}
      </div>
    );
  };

  return (
    <div style={{
      backgroundColor: "rgb(255, 255, 255)",
    }}>
      <HeaderPreview apiData={apiData} />
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}>
        <h1 style={{ fontFamily: 'Helvetica, sans-serif', fontWeight: 700, fontStyle: 'normal', fontSize: '14px', lineHeight: '18px', letterSpacing: '0px', textAlign: 'center', textTransform: 'uppercase', color: '#00577D', padding: "16px 0px", margin: "0px" }}>
          YOUR MINDSET RESULTS
        </h1>

        <SelfMindsetResultImg variables={apiData.variables} />

        <div style={{
          position: "relative",
          background: "#EAF1F5",
          width: "468px",
          padding: "10px",
          borderRadius: "5px",
          boxSizing: "border-box",
          marginTop: "10px"
        }}>
          <div style={{ position: 'absolute', top: '-19px', left: 0, right: 0, height: '19px', overflow: 'visible', zIndex: 2 }}>
            <div style={{ width: 0, height: 0, borderLeft: "6px solid transparent", borderRight: "6px solid transparent", borderBottom: "14px solid #EAF1F5", position: "absolute", left: getArrowPosition(Math.round(percentage)), marginLeft: '-10px', top: "6px" }} />
          </div>
          {renderMindsetBullets(Math.round(percentage))}
          <div style={{
            fontFamily: 'Helvetica, sans-serif',
            fontWeight: 400,
            fontStyle: 'oblique',
            fontSize: '10px',
            lineHeight: '14px',
            letterSpacing: '0px',
            color: '#00577D',
            marginTop: '4px',
            textAlign: 'right'
          }}>
            Credit: Healthy Growing Leaders
          </div>
        </div>

        <div style={{
          marginTop: "16px"
        }}>
          {getMindsetText(Math.round(percentage))}
        </div>
      </div>
      <FooterPreview pageNumber={pageNumber} reportType={reportType} />
    </div>
  );
}
